import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import {
  getQueryKey,
  useGetUserKey,
  useUserKeySuffix,
} from '@/hooks/useQueryKeys';
import { useOsintContext } from '@/state/OsintContext';
import { GraphQuery, MyResource, MyResourceKey, Vulnerability } from '@/types';
import { UseExtendInfiniteQueryOptions, useInfiniteQuery } from '@/utils/api';
import { flattenAssetGraphResponse } from '@/utils/api/flattenAssetGraphResponse';

interface UseMyProps<
  ResourceKey extends MyResourceKey,
  Tree extends boolean = false,
> {
  resource: ResourceKey;
  // query to filter the resource data, if not provided, it will fetch all data
  query?: string | GraphQuery;
  to?: string;
  desc?: boolean;
  global?: boolean;
  tree?: Tree;
  allTenants?: boolean;
  osint?: boolean;
  praetorian?: boolean;
  // user controls which DynamoDB partition is read.
  // - undefined (default): auto — conversation/message use the per-user
  //   partition (classic chat), everything else uses the tenant partition.
  // - true: force user partition.
  // - false: force tenant partition. Used by hunt-iteration conversations
  //   which are tenant-public (Public=true on the row).
  user?: boolean;
}

interface VulnerabilityResponse {
  vulnerabilities: Vulnerability[];
  count: number;
}

export type UseMyOptions<
  ResourceKey extends MyResourceKey,
  Tree extends boolean = false,
> = UseExtendInfiniteQueryOptions<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Tree extends true ? any[] : MyResource[ResourceKey]
> & {
  doNotImpersonate?: boolean;
  email?: string;
};

export const useMy = <
  ResourceKey extends MyResourceKey,
  Tree extends boolean = false,
>(
  props: UseMyProps<ResourceKey, Tree>,
  options?: UseMyOptions<ResourceKey, Tree>
) => {
  // Resolve OSINT mode: explicit prop takes precedence, then context
  const { isOsint: contextOsint } = useOsintContext();
  const osint = props.osint ?? contextOsint;
  const doNotImpersonate = options?.doNotImpersonate ?? osint;

  const axios = useAxios({
    doNotImpersonate,
    email: options?.email,
  });

  // Ensure desc is true for statistics resource
  const desc = props.resource === 'statistic' ? true : props.desc;

  // Opt-in keyset (cursor) pagination. Off (default) = legacy offset paging,
  // byte-identical to before. On = page by an opaque cursor token, with a
  // transparent fall back to offset paging when the query's sort can't engage
  // keyset on the backend (it engages for cursor-supported sorts — a legacy
  // single-field orderBy is bridged into structured OrderByFields server-side;
  // status and grouped sorts fall back to offset).
  //
  // Scope: cursor forwarding is wired for both POST graph-query requests and
  // string-key GET requests. Each sends `cursor: ''` on page 1 and resumes via
  // the echoed token; when the backend can't engage keyset it echoes an `offset`
  // and both paths fall back transparently to offset paging.
  const cursorPagination = options?.cursorPagination === true;

  // Opt-in fetch cancellation (default off). When true, the queryFn consumes
  // TanStack's per-fetch AbortSignal and forwards it to axios, so the request
  // is aborted when the last observer unsubscribes (drawer close, key-remount).
  // Off by default because parts of the app depend on a /my request SETTLING
  // after its component unmounts and warming the cache from an inactive query
  // (ENG-4944's decoupled wait-and-reapply in useModifyAccount).
  const abortOnUnmount = options?.abortOnUnmount === true;

  const response = useInfiniteQuery<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Tree extends true ? any[] : MyResource[ResourceKey]
  >({
    ...options,
    defaultErrorMessage: `Failed to fetch ${props.resource} data`,
    retry: false,
    // The user-scope segment useGetUserKey appends below as the LAST key
    // segment. Handing it to invalidate() makes narrow invalidation keys
    // ([MY, resource, friend]) match query-suffixed keys
    // ([MY, resource, '"query"', friend]) for the SAME principal — plain
    // prefix matching silently misses them under impersonation (ENG-4400).
    userKeySuffix: useUserKeySuffix(doNotImpersonate, options?.email),
    queryKey: useGetUserKey(
      [
        ...getQueryKey.getMy(
          props.resource,
          JSON.stringify(props.query),
          props.to,
          desc,
          props.global,
          props.allTenants,
          osint,
          props.praetorian
        ),
        ...(props.user !== undefined ? [`user:${props.user}`] : []),
        // Keep cursor-mode pages in a distinct cache bucket so toggling
        // cursorPagination never reuses stale offset-paged pages (and vice versa).
        ...(cursorPagination ? ['cursor'] : []),
      ],
      doNotImpersonate,
      options?.email
    ),
    queryFn: async ctx => {
      const { pageParam } = ctx;
      // ENG-4944 relies on /my GETs settling after unmount (decoupled
      // wait-and-reapply warms the cache from an inactive query), so signal
      // consumption — which makes TanStack cancel on last-unsubscribe — is
      // strictly opt-in. TanStack v5 marks the signal "consumed" via a
      // property getter on the queryFn context, so the default path must not
      // touch ctx.signal at all.
      const signal = abortOnUnmount ? ctx.signal : undefined;
      // Conversation/message resources default to the per-user partition
      // (classic chat flow). Callers can override via `user` to hit the
      // tenant partition — used for tenant-public hunt iterations.
      const needsUserScope =
        props.user !== undefined
          ? props.user
          : props.resource === 'conversation' || props.resource === 'message';

      // In cursor mode the page param is `{ offset, cursor }`. The first page
      // carries `cursor: ''` (request the first keyset page); keyset resumes
      // carry the echoed token. When the backend can't engage keyset it echoes
      // an `offset` instead of a `cursor`, so getNextPageParam hands back an
      // offset-only page param and we transparently fall back to offset paging.
      // When cursor mode is off, `pageParam` is the bare offset exactly as
      // before, so `offsetValue === pageParam` and the requests are unchanged.
      const cursorValue =
        cursorPagination &&
        typeof (pageParam as { cursor?: unknown } | undefined)?.cursor ===
          'string'
          ? (pageParam as { cursor: string }).cursor
          : undefined;
      const offsetValue = cursorPagination
        ? (pageParam as { offset?: unknown } | undefined)?.offset
        : pageParam;

      const { data } =
        typeof props.query === 'object'
          ? await axios.post(
              '/my',
              {
                ...props.query,
                ...(cursorValue !== undefined
                  ? { cursor: cursorValue }
                  : { page: offsetValue ? offsetValue : undefined }),
              },
              {
                ...(signal ? { signal } : {}),
                params: {
                  tree: props.tree,
                  label: props.resource,
                  ...(needsUserScope ? { user: 'true' } : {}),
                  ...(props.allTenants ? { allTenants: props.allTenants } : {}),
                  ...(osint ? { osint: 'true' } : {}),
                  ...(props.praetorian ? { praetorian: 'true' } : {}),
                },
              }
            )
          : await axios.get(`/my`, {
              ...(signal ? { signal } : {}),
              params: {
                label: props.resource,
                key:
                  props.query &&
                  (props.query.startsWith('conversation:') ||
                    props.query.startsWith('parent_id:') ||
                    props.query.startsWith('hunt:'))
                    ? props.query
                    : `#${props.resource}${props.query ? (props.query.startsWith('#') ? props.query : '#' + props.query) : ''}`,
                to: props.to,
                ...(desc ? { desc } : {}),
                ...(props.global ? { global: props.global } : {}),
                ...(needsUserScope ? { user: 'true' } : {}),
                // Mirror the POST either/or (line 142): when a keyset cursor is
                // present send it as the RAW string the backend reads from
                // `?cursor=` (offset is JSON-stringified, cursor is not) and omit
                // offset; otherwise send offset exactly as legacy. `cursorValue`
                // is `''` on page 1 (the guard is `!== undefined`, so the
                // empty-string sentinel is preserved) and `undefined` whenever the
                // flag is off — keeping flag-off requests byte-identical.
                // SEC-5 (risk accepted): the cursor rides the URL query string.
                // The token is plain base64 JSON of sort-boundary values, the
                // boundary entity key, a query-hash pin, and — only in
                // cross-partition all-tenants mode — a boundary username; no
                // secrets. Browser history / Referer don't apply to XHR request
                // URLs, and the backend logs the route pattern only (API Gateway
                // access logging is disabled), so there is no server-side URL sink.
                // The one real sink is LogRocket session replay, which already
                // captures the LARGER `?key=` entity-key exposure on this same GET
                // unredacted — the cursor adds no new class of URL exposure.
                // Guardrail: do not add a GET caller that combines
                // cursorPagination with allTenants (the only combination that puts
                // a tenant username in a URL); if one is ever needed, move the
                // token to the POST body or add a LogRocket requestSanitizer for
                // the `cursor`/`key` query params first.
                ...(cursorValue !== undefined
                  ? { cursor: cursorValue }
                  : {
                      offset: offsetValue
                        ? JSON.stringify(offsetValue)
                        : undefined,
                    }),
                ...(props.allTenants ? { allTenants: props.allTenants } : {}),
                ...(osint ? { osint: 'true' } : {}),
                ...(props.praetorian ? { praetorian: 'true' } : {}),
              },
            });

      if (props.tree) {
        const treeData = data === null ? [] : data;
        const limitPerPage =
          typeof props.query === 'object' ? props.query?.limit || 100 : 0;

        return {
          data: treeData,
          offset:
            treeData.length >= limitPerPage
              ? // Use the scalar `offsetValue` (not the raw pageParam): in cursor
                // mode pageParam is a `{offset, cursor}` object and `Number({})` is
                // NaN. tree+cursorPagination is unsupported, but this keeps the
                // legacy offset math intact (offsetValue === pageParam when off).
                data?.offset || (offsetValue ? Number(offsetValue) + 1 : 1)
              : undefined,
        };
      }

      // Handle GraphQuery responses differently from string queries
      if (typeof props.query === 'object') {
        return handleGraphQueryResponse(data, props.resource);
      }

      const resourceData =
        props.resource === 'technology'
          ? data['technologies'] || []
          : props.resource === 'event'
            ? data['traceevents'] || []
            : props.resource === 'workflow_run'
              ? data['workflowruns'] || []
              : data[`${props.resource}s`] || [];

      if (props.resource === 'vulnerability') {
        const vulnResponse = data as VulnerabilityResponse;
        return {
          data: vulnResponse.vulnerabilities,
          offset: undefined,
        };
      }

      if (props.resource === 'asset') {
        return {
          data: resourceData || [],
          offset: data?.offset,
          cursor: data?.cursor,
        };
      }

      if (props.resource === 'seed') {
        return {
          data: resourceData || [],
          offset: data?.offset,
          cursor: data?.cursor,
        };
      }

      return { data: resourceData, offset: data?.offset, cursor: data?.cursor };
    },
    initialPageParam: cursorPagination
      ? { offset: undefined, cursor: '' }
      : undefined,
    getNextPageParam: lastPage => {
      // Continue while EITHER a next offset OR a next cursor is present; stop
      // only when both are absent. A cursor never appears when cursorPagination
      // is off (offset is `omitempty` so it is never a falsy 0 either), so this
      // is byte-identical to the previous `return lastPage.offset` in practice.
      if (!lastPage.offset && !lastPage.cursor) return undefined;
      if (cursorPagination) {
        return { offset: lastPage.offset, cursor: lastPage.cursor };
      }
      return lastPage.offset;
    },
  });

  const hasFetchedData = response.data !== undefined;
  // Memoized on `response.data` (the TanStack Query pages object) so that the
  // returned array reference is stable across renders when the underlying data
  // has not changed. Without this, every render produces a new array literal
  // even if the pages are identical — causing any useCallback / useMemo /
  // useEffect dep chain that consumes the result to see a "changed" dep on
  // every render and potentially loop. See test 'report-up effect does not loop'
  // in SingleScanProfileForm.test.tsx and AssignProfileToAssetsModal.test.tsx.
  const processedData = useMemo(() => {
    if (!hasFetchedData) return [];
    const flat = response.data!.pages.map(({ data }) => data).flat();
    // Defense-in-depth dedup on the DynamoDB row identity `(username, key)`:
    // Neo4j offset pagination can return the same row on two adjacent pages
    // when the sort keys tie, so concatenating pages can surface duplicate
    // items. The backend ORDER BY `key` tiebreaker (PR #6299) that would
    // have prevented this was reverted (PR #6369) for its Neo4j performance
    // cost; cursor-based pagination is the planned backend fix. Until then
    // this frontend dedup is the only active mitigation: it preserves
    // first-occurrence order, but cannot restore rows already dropped at a
    // page boundary. It also silences the `useIndexedRows` "Duplicate key
    // detected" warning, but only for the single-tenant pagination-overlap
    // case: `useIndexedRows` keys on `key` alone, so legitimate cross-tenant
    // rows that repeat a bare `key` are preserved here on purpose and may
    // still trigger that warning in `allTenants` views.
    // The dedup identity is the composite `(username, key)` because
    // `key` alone is NOT globally unique — model keys omit the tenant (e.g.
    // `#setting#display-name`), so an `allTenants` read legitimately returns
    // the same `key` once per tenant; deduping on `key` alone would collapse
    // those distinct-tenant rows. Single-tenant reads are unaffected because
    // username is constant there, reducing the composite to key-only. Items
    // without a truthy string `key` pass through untouched.
    const seen = new Set<string>();
    return flat.filter(item => {
      const k = (item as { key?: unknown }).key;
      if (typeof k !== 'string' || k === '') return true;
      const u = (item as { username?: unknown }).username;
      const id = typeof u === 'string' && u !== '' ? `${u}\0${k}` : k;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [response.data, hasFetchedData]);

  return {
    ...response,
    data: processedData,
    /**
     * True only after the underlying infinite query has produced at least
     * one page response. Use this to distinguish "query is disabled or
     * has never run" from "query ran and returned zero results" — the
     * `data` array is `[]` in both cases.
     *
     * Prefer this over inspecting `data.length` when consumers need that
     * distinction (see AssignProfileToAssetsModal for the canonical use).
     */
    hasFetchedData,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function handleGraphQueryResponse(data: any, resource: MyResourceKey) {
  // For GraphQuery, the response is a GraphQueryResponse
  const graphResponse = data as any; // eslint-disable-line @typescript-eslint/no-explicit-any

  // Check for grouped response (has groups array from groupBy query)
  if (graphResponse.groups && Array.isArray(graphResponse.groups)) {
    // Return grouped response as-is for useGroupedRisks to process
    return {
      data: graphResponse.groups,
      offset: graphResponse.offset,
      cursor: graphResponse.cursor,
      groupBy: graphResponse.groupBy,
    };
  }

  // Extract data based on the resource type
  let resourceData: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any

  switch (resource) {
    case 'asset':
      resourceData = flattenAssetGraphResponse(graphResponse);
      break;
    case 'attribute':
      resourceData = graphResponse.attributes || [];
      break;
    case 'seed':
      resourceData = [
        ...(graphResponse.seeds ? Object.values(graphResponse.seeds) : []),
        ...(graphResponse.webapplications || []),
      ];
      break;
    case 'preseed':
      resourceData = graphResponse.preseeds || [];
      break;
    case 'webpage':
      resourceData = graphResponse.webpages || [];
      break;
    case 'webapplication':
      resourceData = graphResponse.webapplications || [];
      break;
    case 'technology':
      resourceData = graphResponse.technologies || [];
      break;
    case 'risk':
      resourceData = graphResponse.risks || [];
      break;
    case 'vulnerability':
      resourceData = graphResponse.vulnerabilities || [];
      break;
    case 'addomain':
      resourceData = graphResponse.adobjects || [];
      break;
    case 'repository':
      resourceData = graphResponse.repositories || [];
      break;
    case 'parkeddomain':
      resourceData = graphResponse.parkeddomains || [];
      break;
    default:
      // Try to get data using the resource name pattern as fallback
      resourceData = graphResponse[`${resource}s`] || [];
  }

  return {
    data: resourceData,
    offset: graphResponse.offset,
    // Keyset next-page token (top-level `cursor`, omitempty). Threading it here
    // is what lets getNextPageParam engage keyset — without it lastPage.cursor
    // would always be undefined and cursor mode could never advance past page 1.
    cursor: graphResponse.cursor,
  };
}

export const useMyAllTenants = <
  ResourceKey extends MyResourceKey,
  Tree extends boolean = false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TransformResult = any,
>(
  props: UseMyProps<ResourceKey, Tree>,
  transform?: (data: TransformResult[]) => Map<string, TransformResult>
) => {
  const query = useMy({
    ...props,
    allTenants: true,
  });

  const dataMap = transform
    ? transform(query.data)
    : new Map(query.data?.map(item => [item.username, item]));

  return {
    ...query,
    dataMap,
  };
};
